import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap } from 'rxjs';
import {
  SparkAlertDto,
  SparkAlertsDto,
  SparkChartMetric,
  SparkCreatorLeaderboardDto,
  SparkKpiDto,
  SparkReportingDto,
  SparkStructureDto,
  SparkTimeseriesDto,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { SparkAdsReportingService } from '../../services/spark-ads-reporting.service';
import { SparkAdsStore } from '../../services/spark-ads-store';
import { SPARK_RANGE_PRESETS, SparkLoadState, SparkRangePreset } from '../../spark-ads.types';
import { formatCurrency, formatDelta, formatKpiValue, formatMultiplier } from '../../spark-ads.format';
import { DailyPerformanceChart } from './atoms/daily-performance-chart/daily-performance-chart';
import { CampaignStructureTree } from './atoms/campaign-structure-tree/campaign-structure-tree';
import { AdsEmptyState } from './atoms/ads-empty-state/ads-empty-state';

/**
 * "Is my money working?" at a glance, then a way into the campaign tree.
 *
 * Every figure comes from TikTok `report/integrated/` through the API. When a brief has
 * no Spark campaign the whole body is replaced by the first-run state.
 */
@Component({
  selector: 'sa-ads-dashboard',
  imports: [DailyPerformanceChart, CampaignStructureTree, AdsEmptyState],
  templateUrl: './ads-dashboard.html',
  styleUrl: './ads-dashboard.scss',
  host: { class: 'block w-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdsDashboard {
  readonly briefId = input.required<string>();

  private readonly reportingService = inject(SparkAdsReportingService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(SparkAdsStore);

  protected readonly rangePresets = SPARK_RANGE_PRESETS;

  protected readonly reportingState = signal<SparkLoadState>('idle');
  protected readonly reporting = signal<SparkReportingDto | undefined>(undefined);
  /**
   * True when the brief has no Spark campaign at all. Distinguished from a genuine
   * failure so the first-run state shows instead of an error.
   */
  protected readonly hasNoCampaign = signal(false);
  protected readonly reportingError = signal<string | null>(null);

  protected readonly seriesState = signal<SparkLoadState>('idle');
  protected readonly series = signal<SparkTimeseriesDto | undefined>(undefined);

  protected readonly structureState = signal<SparkLoadState>('idle');
  protected readonly structure = signal<SparkStructureDto | undefined>(undefined);

  protected readonly leaderboard = signal<SparkCreatorLeaderboardDto | undefined>(undefined);
  protected readonly alerts = signal<SparkAlertsDto | undefined>(undefined);

  private readonly loadHeadline$ = new Subject<void>();
  private readonly loadSeries$ = new Subject<void>();

  protected readonly currencyCode = computed(() => this.reporting()?.currencyCode ?? null);

  protected readonly kpis = computed<SparkKpiDto[]>(() => this.reporting()?.kpis ?? []);

  protected readonly summaryLine = computed(() => {
    const report = this.reporting();
    if (!report) {
      return '';
    }
    const groups = `${report.adGroupCount} ad ${report.adGroupCount === 1 ? 'group' : 'groups'}`;
    const ads = `${report.adCount} Spark ${report.adCount === 1 ? 'ad' : 'ads'}`;
    return `${report.campaignCount} campaign · ${groups} · ${ads}`;
  });

  protected readonly topCreators = computed(() => this.leaderboard()?.rows.slice(0, 5) ?? []);

  protected readonly alertRows = computed<SparkAlertDto[]>(() => this.alerts()?.alerts ?? []);

  constructor() {
    this.loadHeadline$
      .pipe(
        switchMap(() => {
          const briefId = this.briefId();
          const range = this.store.dateRange();
          this.reportingState.set('loading');
          this.structureState.set('loading');
          this.reportingError.set(null);

          return this.reportingService.getReporting(briefId, range).pipe(
            catchError((error: unknown) => {
              // "No Spark Campaign found" is the first-run case, not a failure.
              if (this.isMissingCampaign(error)) {
                this.hasNoCampaign.set(true);
                this.reportingState.set('loaded');
                this.structureState.set('loaded');
              } else {
                this.reportingError.set(this.messageFor(error));
                this.reportingState.set('error');
                this.structureState.set('error');
              }
              return of(undefined);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((report) => {
        if (!report) {
          return;
        }
        this.hasNoCampaign.set(false);
        this.reporting.set(report);
        this.reportingState.set('loaded');
        this.loadSupportingPanels();
      });

    this.loadSeries$
      .pipe(
        switchMap(() => {
          this.seriesState.set('loading');
          return this.reportingService
            .getTimeseries(this.briefId(), this.store.chartMetric(), this.store.dateRange())
            .pipe(
              catchError(() => {
                this.seriesState.set('error');
                return of(undefined);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((series) => {
        if (series) {
          this.series.set(series);
          this.seriesState.set('loaded');
        }
      });

    /** Re-fetch whenever the date range changes; the chart also tracks its metric. */
    effect(() => {
      this.store.dateRange();
      this.loadHeadline$.next();
    });

    effect(() => {
      this.store.dateRange();
      this.store.chartMetric();
      this.loadSeries$.next();
    });
  }

  protected reload(): void {
    this.loadHeadline$.next();
    this.loadSeries$.next();
  }

  protected selectRange(preset: SparkRangePreset): void {
    this.store.setRangePreset(preset);
  }

  protected selectMetric(metric: SparkChartMetric): void {
    this.store.setChartMetric(metric);
  }

  protected goToBuilder(): void {
    this.store.setView('new');
  }

  protected goToVideos(): void {
    this.store.setView('videos');
  }

  protected getRangeClass(preset: SparkRangePreset): string {
    return this.store.rangePreset() === preset
      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  // --- KPI presentation -----------------------------------------------------

  protected kpiValue(kpi: SparkKpiDto): string {
    return formatKpiValue(kpi.value, kpi.format, this.currencyCode());
  }

  protected kpiDelta(kpi: SparkKpiDto): string {
    return formatDelta(kpi.deltaPercent);
  }

  /**
   * Delta chip colour follows `deltaIsGood`, which the API sets per metric — a falling
   * cost per result is an improvement even though the number went down.
   */
  protected kpiDeltaClass(kpi: SparkKpiDto): string {
    if (kpi.deltaIsGood === null) {
      return 'pill pill-gray';
    }
    return kpi.deltaIsGood ? 'pill pill-green' : 'pill pill-red';
  }

  protected creatorRoas(roas: number): string {
    return formatMultiplier(roas);
  }

  protected creatorMeta(row: { adCount: number; spend: number }): string {
    const ads = `${row.adCount} Spark ${row.adCount === 1 ? 'ad' : 'ads'}`;
    return `${ads} · ${formatCurrency(row.spend, this.currencyCode(), 0)} spend`;
  }

  protected alertToneClass(alert: SparkAlertDto): string {
    switch (alert.tone) {
      case 'warning':
        return 'border-amber-500/25 bg-amber-50 dark:bg-amber-900/20';
      case 'opportunity':
        return 'border-brand-studio/25 bg-brand-studio/5';
      default:
        return 'border-border bg-muted/50';
    }
  }

  protected alertDotClass(alert: SparkAlertDto): string {
    switch (alert.tone) {
      case 'warning':
        return 'bg-amber-500';
      case 'opportunity':
        return 'bg-brand-studio';
      default:
        return 'bg-gray-400';
    }
  }

  /** Expiring authorisations are actioned in the video library. */
  protected onAlertAction(alert: SparkAlertDto): void {
    if (alert.kind === 'authExpiring') {
      this.store.setView('videos');
    }
  }

  // --- Internals ------------------------------------------------------------

  private loadSupportingPanels(): void {
    const briefId = this.briefId();
    const range = this.store.dateRange();

    this.reportingService
      .getStructure(briefId, range)
      .pipe(
        catchError(() => {
          this.structureState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((structure) => {
        if (structure) {
          this.structure.set(structure);
          this.structureState.set('loaded');
        }
      });

    // The rail panels are secondary: a failure leaves them empty rather than shouting.
    this.reportingService
      .getCreatorLeaderboard(briefId, range)
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((leaderboard) => leaderboard && this.leaderboard.set(leaderboard));

    this.reportingService
      .getAlerts(briefId, range)
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((alerts) => alerts && this.alerts.set(alerts));
  }

  private isMissingCampaign(error: unknown): boolean {
    const message = this.messageFor(error).toLowerCase();
    return message.includes('no spark campaign');
  }

  private messageFor(error: unknown): string {
    const candidate = error as { error?: { message?: string }; message?: string };
    return candidate?.error?.message ?? candidate?.message ?? 'Something went wrong.';
  }
}
